import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import UserApi from "api/userApi";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import ModalPreview from "components/Common/ModalPreview";
import {
  COLOR_APPLICATION_STATUS,
  LIST_OPTION_APPLICATION_STATUS,
  LIST_OPTION_GENDER,
} from "helpers/constans";
import { formatDateStringToKorea, formatDateStringToUTC } from "helpers/format";
import CountUp from "react-countup";
import Detail from "./Detail";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import { getApplications } from "store/application/thunk";
import { getCompanies } from "store/thunks";
import { getFields } from "store/fields/thunk";
import fieldsApi from "api/fieldsApi";
import avatar1 from "../../assets/images/users/ic_defaul_avatar2.png";
import CompanyImg from "../../assets/images/Company.png";
import AuditionImg from "../../assets/images/Audition.png";
import applicationApi from "api/applicationApi";
import CopyWrapper from "components/Common/CopyWrapper";
import { useRole } from "components/Hooks/UserHooks";
import DropdownAsyncCompanies from "components/Common/DropdownAsyncCompanies";

interface Option {
  label: string;
  value: string;
}

registerLocale("en", en);
registerLocale("ko", ko);

const ApplicationManagement = () => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();
  const { userPermissions } = useRole();
  
  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    audition_title: withDefault(StringParam, ""),
    user_phone_number: withDefault(StringParam, ""),
    user_email: withDefault(StringParam, ""),
    user_name: withDefault(StringParam, ""),
    user_gender: withDefault(StringParam, ""),
    company_id: withDefault(StringParam, ""),
    field_id: withDefault(StringParam, ""),
    field_role_group_id: withDefault(StringParam, ""),
    application_status: withDefault(StringParam, ""),
    created_to: withDefault(StringParam, ""),
    created_from: withDefault(StringParam, ""),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [summary, setSummary] = useState<any>(null);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [itemToEdit, setItemToEdit] = useState<any>(false);

  const [titleSearch, setTitleSearch] = useState<string>(
    query?.audition_title || ""
  );

  const [nickNameSearch, setNicknameSearch] = useState<string>(
    query?.nickname || ""
  );
  const [phoneSearch, setPhoneSearch] = useState<string>(query?.nickname || "");

  const [roleGroupSearch, setRoleGroupSearch] = useState<Option | any>("");
  const [genderSearch, setGenderSearch] = useState<Option | any>(
    LIST_OPTION_GENDER?.find((item) => item?.value === query?.status)?.value ??
      ""
  );
  const [dataRoleGroup, setDataRoleGroup] = useState<Option | any>([]);

  const [emailSearch, setEmailSearch] = useState<string>(query?.email || "");

  const [statusSearch, setStatusSearch] = useState<any>(
    LIST_OPTION_APPLICATION_STATUS?.find(
      (item) => item?.value === query?.status
    )?.value ?? ""
  );

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.start_date
      ? moment(query?.start_date || "", "Y-MM-DD").toDate()
      : null,
    query?.end_date ? moment(query?.end_date || "", "Y-MM-DD").toDate() : null,
  ]);
  const [start_date, end_date] = dateSearch;

  const [linkImage, setLinkImage] = useState<string>("");
  // const [countrySearch, setCountrySearch] = useState<any>("kr");

  const selectLayoutState = (state: any) => state.Application;
  const ApplicationProperties = createSelector(selectLayoutState, (state) => ({
    applications: state?.Applications,
    isApplicationLoading: state.isApplicationLoading,
    isApplicationSuccess: state.isApplicationSuccess,

    error: state.error,
  }));

  const companies: any = useSelector(
    (state: any) => state?.Company?.companies?.items
  );
  const COMPANIES = useMemo(() => {
    return (
      companies?.map((item: any) => ({
        label: item?.name,
        value: item?.id,
      })) || []
    );
  }, [companies]);

  const [companySearch, setCompanySearch] = useState<any>(query?.company_id);

  const fields: any = useSelector((state: any) => state?.Fields?.Fields?.items);
  const FIELDS = useMemo(() => {
    return (
      fields?.map((item: any) => ({
        label: item?.title,
        value: item?.id,
      })) || []
    );
  }, [fields]);

  const [fieldSearch, setFieldSearch] = useState<Option | any>(
    FIELDS?.find((item: any) => item?.value === query?.status)?.value ?? ""
  );

  // Inside your component
  const { applications, isApplicationLoading } = useSelector(
    ApplicationProperties
  );

  useEffect(() => {
    dispatch(getApplications(query));
    initSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      user_name: nickNameSearch.trim() || "",
      user_email: emailSearch.trim() || "",
      audition_title: titleSearch.trim() || "",
      user_phone_number: phoneSearch.trim() || "",
      user_gender: genderSearch || "",
      company_id: (companySearch && companySearch?.value !== "all") ? companySearch?.value : "",
      field_id: fieldSearch || "",
      field_role_group_id: roleGroupSearch || "",
      application_status: statusSearch || "",
      created_from: start_date
        ? formatDateStringToUTC(
            moment(new Date(start_date))
              .startOf("day")
              .format("Y-MM-DD HH:mm:ss")
          )
        : "",
      created_to: end_date
        ? formatDateStringToUTC(
            moment(new Date(end_date)).endOf("day").format("Y-MM-DD HH:mm:ss")
          )
        : "",

      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        audition_title: "",
        user_phone_number: "",
        user_email: "",
        user_name: "",
        user_gender: "",
        company_id: "",
        field_id: "",
        field_role_group_id: "",
        application_status: "",
        created_from: "",
        created_to: "",
        sort_by: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setNicknameSearch((_prev) => "");
    setTitleSearch((_prev) => "");
    setPhoneSearch((_prev) => "");
    setCompanySearch(() => "");
    setFieldSearch(() => "");
    setRoleGroupSearch(() => "");
    setGenderSearch(() => "");
    setEmailSearch("");

    setStatusSearch("");
    setDateSearch([null, null]);
  };

  // Begin::Update User
  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(getApplications(query));
  };
  const getDataRoleGroup = async (id: any) => {
    try {
      const res = await fieldsApi.getRoleGroup(id);
      const arr =
        res?.data?.items?.map((item: any) => ({
          label: item?.title,
          value: item?.id,
        })) || [];
      setDataRoleGroup(arr || []);
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (fieldSearch) {
      getDataRoleGroup(fieldSearch);
      setRoleGroupSearch("");
    }
  }, [fieldSearch]);
  //End::Show Notification

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div style={{ width: 35 }}>
              {applications?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },

      {
        Header: t("status"),
        accessor: "status",
        thWidth: 80,
        filterable: false,
        sortable: false,
        thClass: "text-center",
        Cell: (cell: any) => {
          const status = cell?.value;
          return (
            <div className="text-center">
              <span
                className={`rounded-pill badge bg-${
                  COLOR_APPLICATION_STATUS[status] || "secondary"
                }`}
              >
                {t(
                  LIST_OPTION_APPLICATION_STATUS?.find(
                    (item: any) => String(item?.value) === String(status)
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("audition_thumbnail"),
        accessor: "audition.thumbnail_url",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="text-center">
              <img
                src={cell?.value || AuditionImg}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = AuditionImg;
                }}
                style={{ height: "60px" }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Auditon Title"),
        accessor: "audition.title",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <CopyWrapper
              contentCopy={cell?.value || ""}
              style={{
                width: "360px",
                display: "inline-flex",
                minWidth: "60px",
              }}
            >
              <div className="cursor-pointer ellipsis-2-lines">
                {cell?.value ?? "-"}
              </div>
            </CopyWrapper>
          );
        },
      },
      {
        Header: t("audition_start_date"),
        accessor: "audition.application_start_date",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("audition_end_date"),
        accessor: "audition.application_end_date",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("company_logo"),
        accessor: "company.logo_url",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="text-center">
              <img
                src={cell?.value || CompanyImg}
                alt="thumbnail"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = CompanyImg;
                }}
                style={{ height: "60px" }}
              />
            </div>
          );
        },
      },
      {
        Header: t("company_name"),
        accessor: "company.name",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="whitespace-nowrap">{`${cell?.value || ""}`}</div>
          );
        },
      },
      {
        Header: t("avatar"),
        accessor: "user_profile.image_url",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div>
              <img src={cell?.value || avatar1} style={{ height: "60px" }} />
            </div>
          );
        },
      },
      {
        Header: t("full_name"),
        accessor: "user_profile.fullname",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="whitespace-nowrap">{`${cell?.value || ""}`}</div>
          );
        },
      },
      {
        Header: t("Email"),
        accessor: "user_profile.email",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="whitespace-nowrap">{`${cell?.value || ""}`}</div>
          );
        },
      },
      {
        Header: t("gender"),
        accessor: "user_profile.gender",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="whitespace-nowrap">
              {t(
                LIST_OPTION_GENDER.find((i) => i.value === cell?.value)
                  ?.label || ""
              )}
            </div>
          );
        },
      },
      {
        Header: t("phone_number"),
        accessor: "user_profile.phone_number",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="whitespace-nowrap">{`${cell?.value || ""}`}</div>
          );
        },
      },
      {
        Header: t("audition_position"),
        accessor: "roles",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <div className="d-flex flex-wrap align-items-center gap-1">
              {cell?.value?.map((i: any, index: number) => (
                <div
                  key={index}
                  className="d-flex align-items-center gap-1 bg-primary badge rounded-pill"
                >
                  {
                    [i?.field?.title, i?.field_role_group?.title, i?.audition_role?.title].filter(Boolean).join(" - ")
                  }
                </div>
              ))}
            </div>
          );
        },
      },

      {
        Header: t("Created at"),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("action"),
        thClass: "text-center",
        thWidth: 100,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "70px" }}
            >
              <TooltipCustom
                title={t("Button Update Operator")}
                id={`update-us-${item?.id}`}
              >
                <div
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  onClick={(e) => {
                    console.log("e", e);
                    e.preventDefault();
                    setIsOpen(true);
                    setItemToEdit(item);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom"></i>
                </div>
              </TooltipCustom>
              <TooltipCustom
                title={t("Button Delete Operator")}
                id={`detail-cp-${item?.id}`}
              >
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      console.log("e", e);
                      // e.preventDefault();
                      // handleConfirmDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, applications?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };
  // Begin::Delete

  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await UserApi?.deleteUser(id);
      if (response?.success) {
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const initSummary = async () => {
    try {
      const res = await applicationApi.getSummary(query);
      setSummary(res?.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  // End::Delete

  useEffect(() => {
    dispatch(getFields({ limit: 1000 }));

    document.title = `${t("User Management")} | ShowStar`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  // useEffect(() => {
  //   const handleCallAllOption = async () => {
  //     try {
  //       const [resCountry]: any = await Promise.all([
  //         countryApi.countries({ page: 1, limit: 20000 }),
  //       ]);
  //       setListCountry((_prev: any) => resCountry?.data || []);
  //       if (query?.country) {
  //         const _country = resCountry?.data?.find(
  //           (_c: any) => String(_c?.code) === String(query?.country)
  //         );
  //         setCountrySearch((_prev: any) =>
  //           _country ? { value: _country?.code, label: _country?.name } : null
  //         );
  //       }
  //     } catch (error: any) {
  //       return error;
  //     }
  //   };

  //   handleCallAllOption();
  // }, []);

  const onCloseImageClick = () => {
    setLinkImage((_prev) => "");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Application Management")}
            pageTitle={t("Home")}
          />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <div className="d-block d-xl-flex justify-content-between align-items-start">
                    <div
                      className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                      style={{ maxWidth: "100%" }}
                    >
                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.ALL || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Applied")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.APPLIED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Screening Passed")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.SCREENING_PASSED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Screening Failed")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.SCREENING_FAILED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Audition Scheduled")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.AUDITION_SCHEDULED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Audition Completed")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.AUDITION_COMPLETED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Final Passed")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.FINAL_PASSED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Rejected")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.REJECTED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "200px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("User Canceled")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={summary?.CANCELED || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>

                    {/* <div className="col-sm-auto text-end">
                      <button
                        type="button"
                        className="btn btn-success d-md-block"
                        onClick={() => {
                          setIsOpen(true);
                          setItemToEdit(null);
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Button Create Application")}
                      </button>
                    </div> */}
                  </div>
                </CardHeader>

                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("audition_title")}`}
                          isShow={!!titleSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("audition_title")}...`}
                            value={titleSearch}
                            onChange={(e) => setTitleSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("full_name")}`}
                          isShow={!!nickNameSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("full_name")}...`}
                            value={nickNameSearch}
                            onChange={(e) => setNicknameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("phone_number")}`}
                          isShow={!!phoneSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("phone_number")}...`}
                            value={phoneSearch}
                            onChange={(e) => setPhoneSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Email")} isShow={!!emailSearch}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email")}...`}
                            value={emailSearch}
                            onChange={(e) => setEmailSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={`${t("Gender")}`} isShow={true}>
                          <DropdownStatusNew
                            name="gender"
                            dataList={LIST_OPTION_GENDER}
                            placeholder={`${t("select_gender")}...`}
                            className="dropdown-status-rounded p-0"
                            classNamePrefix="name-prefix"
                            initialValue={genderSearch}
                            onChangeSelect={(event: any) =>
                              setGenderSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            // colors={COLOR_BAR_LIST_OPEN_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      {
                        userPermissions !== "COMPANY" && (
                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-2"
                          >
                            <LabelWrapper label={t("Company")} isShow={true}>
                              <DropdownAsyncCompanies
                                name="company_id"
                                onChangeSelect={(event: any) => {
                                  setCompanySearch(event)
                                }}
                                isMulti={false}
                                placeholder={`${t("Select Company")}...`}
                                isShowIconCheck={false}
                                listCheckedUsers={companySearch}
                                isAllOption={true}
                                isClearable={false}
                              />
                            </LabelWrapper>
                          </Col>
                        )
                      }

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("fieldCategories")}`}
                          isShow={true}
                        >
                          <DropdownStatusNew
                            name="field_category "
                            dataList={FIELDS}
                            placeholder={`${t("select_field_category")}...`}
                            className="dropdown-status-rounded p-0"
                            classNamePrefix="name-prefix"
                            initialValue={fieldSearch}
                            onChangeSelect={(event: any) =>
                              setFieldSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            // colors={COLOR_BAR_LIST_OPEN_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Role Group")}`}
                          isShow={true}
                        >
                          <DropdownStatusNew
                            name="role_group "
                            dataList={dataRoleGroup}
                            placeholder={`${t("select_role_group")}...`}
                            className="dropdown-status-rounded p-0"
                            classNamePrefix="name-prefix"
                            initialValue={roleGroupSearch}
                            onChangeSelect={(event: any) =>
                              setRoleGroupSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            // colors={COLOR_BAR_LIST_OPEN_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("status")} isShow={true}>
                          <DropdownStatusNew
                            name="status"
                            dataList={LIST_OPTION_APPLICATION_STATUS || []}
                            placeholder={`${t("status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) =>
                              setStatusSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_APPLICATION_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-3 mt-md-3 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!start_date || !!end_date}
                        >
                          <DatePickerCustom
                            startDate={start_date || null}
                            endDate={end_date || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>
                      
                      <Col
                        sm={12}
                        md={`${userPermissions === "COMPANY" ? 9 : 6}`}
                        lg={`${userPermissions === "COMPANY" ? 9 : 6}`}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={
                        applications?.items?.length ? applications?.items : []
                      }
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={applications?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(applications?.pagination?.total) /
                          Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: query.sort_by,
                        order_by: query.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isApplicationLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* <Modal
                    isOpen={!!itemToEdit?._id || isOpen}
                    centered={true}
                    size="xl"
                    scrollable={true}
                >
                    <ModalHeader toggle={onCloseClick}>
                        <>
                            {!!itemToEdit?._id
                                ? t("Button Update User")
                                : t("Button Create User")}
                        </>{" "}
                        <>
                            {itemToEdit?._id && (" - " + (itemToEdit?.nickname || "") || "")}
                        </>
                    </ModalHeader>
                    <ModalBody>
                        <div style={{ minHeight: "calc(100dvh - 200px)" }}>
                            <UserForm
                                isModal={true}
                                id={String(itemToEdit?._id || "")}
                                isCopy={!!itemToEdit?.isCopy}
                                triggerRefresh={triggerRefresh}
                                LIST_OPTION_ROLE={LIST_OPTION_ROLE_LANG}
                            />
                        </div>
                    </ModalBody>
                </Modal> */}

        {itemToEdit && (
          <Detail
            isOpen={isOpen}
            item={itemToEdit}
            onClose={onCloseClick}
            triggerRefresh={triggerRefresh}
          />
        )}

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete User")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?._id)}
          />
        )}

        <ModalPreview
          type={"IMAGE"}
          isOpen={!!linkImage}
          url={linkImage}
          onClose={() => onCloseImageClick()}
        />
      </div>
    </React.Fragment>
  );
};

export default ApplicationManagement;
